import { useEffect, useRef, useState } from 'react';
import { GoogleMap, LatLngBounds } from '@capacitor/google-maps';
import type { DishListing } from '../../types/listing';
import { buildDishMarkerIcon } from '../../utils/dishMarkerIcon';
import { BASE_MAP_CONFIG, getBaseMapLocale } from '../../utils/mapConfig';
import {
  hasMapAuthFailed,
  logMapError,
  resolveFailureReason,
  subscribeMapAuthFailure,
  type MapFailureReason,
  type MapStatus,
} from '../../utils/mapDiagnostics';

const MAP_NAME = 'HomeFeedMap';

// Initial map zoom — also the value clustering math falls back on before the
// first camera-idle event arrives.
const INITIAL_ZOOM = 12;

type Props = {
  listings: DishListing[];
  /** User's current location, used to center the camera on first creation. */
  userLocation?: { lat: number; lon: number } | null;
  /** Called when a marker is tapped. */
  onMarkerSelect?: (listing: DishListing) => void;
  /**
   * Called when a cluster tap resolves to listings that share (effectively)
   * the same coordinate, so zoom-in can't separate them. Parent is expected
   * to surface a picker UI for the listings.
   */
  onClusterSelect?: (listings: DishListing[]) => void;
  /** Called when the map background is tapped. */
  onMapTap?: () => void;
  /**
   * Called as the map moves through loading → ready / failed. A failure can
   * still flip to ready (e.g. a slow create finishing after the timeout).
   * To retry after a failure, remount this component (change its `key`).
   */
  onStatusChange?: (status: MapStatus) => void;
  className?: string;
};

const FALLBACK_CENTER = { lat: 42.9634, lng: -85.6681 };

// Floor zoom for the fit-to-listings camera. Listings that fall outside the
// frame at this zoom are clipped rather than zooming out further — keeps the
// markers from shrinking to dots for a single far-flung outlier.
const MIN_ZOOM = 10;
// Zoom used when there's effectively one point to show (one listing, or all
// listings collapse onto the same obfuscated coord).
const SINGLE_LISTING_ZOOM = 14;
// Padding (px) around the fitted bounds.
const FIT_PADDING_PX = 80;
// Lat/lng span below which we treat the bounds as a single point.
const SAME_POINT_EPSILON = 0.0001;

// Hand-rolled proximity clustering. We do this instead of using
// `@capacitor/google-maps`' `enableClustering` because that API doesn't let us
// customize the cluster pin appearance — we want clusters to look like our
// dish marker with a count badge, identical to a same-coord stack.
//
// Target the same ~60-pixel cluster radius MarkerClusterer's defaults use.
const CLUSTER_RADIUS_PX = 60;
// Re-cluster only when zoom changes by at least this much, so pure pans
// don't trigger marker rebuilds.
const ZOOM_QUANTIZATION = 0.5;

// If the map hasn't finished creating this long after mount, log it and
// report a `timeout` failure. This is the catch-all for gray-screen reports:
// an SDK call that hangs forever never throws, so only a timer can see it.
// Measured from mount, so the automatic retry below can't stretch it.
const READY_TIMEOUT_MS = 10_000;
// A create that throws gets one quick automatic retry before the parent is
// told it failed. Covers transient plugin/SDK races without making the user
// sit through repeated attempts.
const MAX_CREATE_ATTEMPTS = 2;
const CREATE_RETRY_DELAY_MS = 500;

// How far map creation got. Reported with failures so a hang or crash can
// be pinned to a specific step.
type CreateStage = 'scheduled' | 'creating' | 'listeners' | 'ready';

function parseObfuscated(s: string | null | undefined): { lat: number; lng: number } | null {
  if (!s) return null;
  const [lat, lng] = s.split(',').map(Number);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
}

function listingsToCoords(listings: DishListing[]): { lat: number; lng: number }[] {
  return listings
    .map((l) => parseObfuscated(l.obfuscatedLocation))
    .filter((c): c is { lat: number; lng: number } => c !== null);
}

// Approximate distance in meters between two lat/lng points using equirectangular
// projection — fine for the small spans we cluster over (< a few km).
function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const METERS_PER_DEG_LAT = 111_000;
  const avgLat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const cosLat = Math.cos(avgLat);
  const dLat = (a.lat - b.lat) * METERS_PER_DEG_LAT;
  const dLng = (a.lng - b.lng) * METERS_PER_DEG_LAT * cosLat;
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

// Web Mercator: at zoom Z, 1 pixel ≈ 156_543 / 2^Z meters at the equator,
// scaled by cos(lat). Use cos(40°) ≈ 0.77 as a midlatitude approximation —
// good enough for the cluster-radius heuristic.
function clusterRadiusMeters(zoom: number): number {
  return (CLUSTER_RADIUS_PX * 156_543 * 0.77) / Math.pow(2, zoom);
}

type CoordGroup = {
  listings: DishListing[];
  coord: { lat: number; lng: number };
};

// Greedy radius-based clustering. Distance is always measured from the seed
// (not the running centroid) so the cluster doesn't "drift" into absorbing
// markers beyond the original radius from the seed.
function clusterByProximity(groups: CoordGroup[], zoom: number): CoordGroup[] {
  const R = clusterRadiusMeters(zoom);
  const remaining = [...groups];
  const out: CoordGroup[] = [];

  while (remaining.length > 0) {
    const seed = remaining.pop()!;
    const listings = [...seed.listings];
    // Weighted centroid by listing count, so the cluster pin lands closer to
    // where most of the listings actually are.
    let totalWeight = seed.listings.length;
    let sumLat = seed.coord.lat * totalWeight;
    let sumLng = seed.coord.lng * totalWeight;

    for (let i = remaining.length - 1; i >= 0; i--) {
      const candidate = remaining[i];
      if (distanceMeters(seed.coord, candidate.coord) <= R) {
        const w = candidate.listings.length;
        sumLat += candidate.coord.lat * w;
        sumLng += candidate.coord.lng * w;
        totalWeight += w;
        listings.push(...candidate.listings);
        remaining.splice(i, 1);
      }
    }

    out.push({
      listings,
      coord: { lat: sumLat / totalWeight, lng: sumLng / totalWeight },
    });
  }

  return out;
}

function computeBounds(coords: { lat: number; lng: number }[]) {
  if (coords.length === 0) return null;
  let minLat = coords[0].lat;
  let maxLat = coords[0].lat;
  let minLng = coords[0].lng;
  let maxLng = coords[0].lng;
  for (const c of coords) {
    if (c.lat < minLat) minLat = c.lat;
    if (c.lat > maxLat) maxLat = c.lat;
    if (c.lng < minLng) minLng = c.lng;
    if (c.lng > maxLng) maxLng = c.lng;
  }
  return {
    southwest: { lat: minLat, lng: minLng },
    northeast: { lat: maxLat, lng: maxLng },
    center: { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 },
  };
}

/**
 * Renders just the Google Map with dish markers. No modal, no card overlay —
 * parents own that. Markers re-sync whenever `listings` changes.
 */
export const HomeFeedMapView: React.FC<Props> = ({
  listings,
  userLocation,
  onMarkerSelect,
  onClusterSelect,
  onMapTap,
  onStatusChange,
  className,
}) => {
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMap | null>(null);
  // Each marker may represent ONE listing (single) or N listings (a same-coord
  // stack). Tap handler chooses between onMarkerSelect / onClusterSelect by
  // group size.
  const markerToListingsRef = useRef<Map<string, DishListing[]>>(new Map());
  const currentMarkerIdsRef = useRef<string[]>([]);
  const [mapReady, setMapReady] = useState(false);
  // Drives re-clustering: when the user zooms, the camera-idle listener
  // updates this and the marker-sync effect re-runs at the new zoom level.
  // Quantized in the listener to avoid churn on tiny zoom drift.
  const [currentZoom, setCurrentZoom] = useState<number>(INITIAL_ZOOM);
  const createStageRef = useRef<CreateStage>('scheduled');

  // Latest callbacks via ref so the create-effect doesn't re-run.
  const onMarkerSelectRef = useRef(onMarkerSelect);
  onMarkerSelectRef.current = onMarkerSelect;
  const onClusterSelectRef = useRef(onClusterSelect);
  onClusterSelectRef.current = onClusterSelect;
  const onMapTapRef = useRef(onMapTap);
  onMapTapRef.current = onMapTap;
  const userLocationRef = useRef(userLocation);
  userLocationRef.current = userLocation;
  const listingsRef = useRef(listings);
  listingsRef.current = listings;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  // Create/destroy the map once per mount.
  useEffect(() => {
    let cancelled = false;
    let createdMap: GoogleMap | null = null;
    let retryId: number | undefined;
    const mountedAt = Date.now();
    let watchdogFired = false;
    createStageRef.current = 'scheduled';

    const reportStatus = (status: MapStatus) => {
      if (!cancelled) onStatusChangeRef.current?.(status);
    };
    const fail = (reason: MapFailureReason) => {
      reportStatus({ state: 'failed', reason: resolveFailureReason(reason) });
    };

    // Google already rejected the key this session; skip straight to the
    // fallback rather than loading a map that will paint Google's error box.
    if (hasMapAuthFailed()) {
      fail('config');
      return () => {
        cancelled = true;
      };
    }
    reportStatus({ state: 'loading' });
    // Can fire even after the map reports ready: Google inits, then rejects
    // the key and paints its own overlay on top.
    const unsubscribeAuth = subscribeMapAuthFailure(() => fail('config'));

    // Context attached to every create-path failure. Element size matters:
    // a 0x0 container renders as a gray box with no error at all.
    const createContext = () => {
      const rect = mapElRef.current?.getBoundingClientRect();
      return {
        createStage: createStageRef.current,
        elapsedMs: Date.now() - mountedAt,
        elementWidth: rect ? Math.round(rect.width) : null,
        elementHeight: rect ? Math.round(rect.height) : null,
        listingCount: listingsRef.current.length,
        hasUserLocation: !!userLocationRef.current,
      };
    };

    const watchdogId = window.setTimeout(() => {
      if (cancelled || createStageRef.current === 'ready') return;
      watchdogFired = true;
      logMapError(
        MAP_NAME,
        'ready-watchdog',
        new Error(`map not ready after ${READY_TIMEOUT_MS}ms`),
        createContext(),
      );
      fail('timeout');
    }, READY_TIMEOUT_MS);

    const attempt = (attemptNumber: number) => {
      create().catch(async (e) => {
        if (cancelled) return;
        logMapError(MAP_NAME, 'create', e, { ...createContext(), attempt: attemptNumber });
        // Tear down a half-built instance (e.g. a listener call threw after
        // GoogleMap.create resolved) before trying again.
        if (createdMap) {
          const stale = createdMap;
          createdMap = null;
          mapInstanceRef.current = null;
          try {
            await stale.destroy();
          } catch {
            // already broken — nothing more to clean up
          }
        }
        if (cancelled) return;
        if (attemptNumber < MAX_CREATE_ATTEMPTS) {
          retryId = window.setTimeout(() => attempt(attemptNumber + 1), CREATE_RETRY_DELAY_MS);
        } else {
          fail('error');
        }
      });
    };

    // NOTE(maps): match the StrictMode-safe defer pattern used by
    // ListingLocationMap — Capacitor Maps races on rapid create/destroy.
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      attempt(1);
    }, 0);

    const create = async () => {
      // Config problems resolve (not throw) so they skip the automatic
      // retry: a missing element or key won't fix itself in 500ms.
      if (!mapElRef.current) {
        logMapError(MAP_NAME, 'create', new Error('map element not mounted'), createContext());
        fail('config');
        return;
      }
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        logMapError(MAP_NAME, 'create', new Error('VITE_GOOGLE_MAPS_API_KEY is not set'), createContext());
        fail('config');
        return;
      }

      // Prefer the listings centroid for the initial frame so the post-create
      // fit-to-bounds doesn't visibly snap from a different position. Falls
      // back to user location, then a regional fallback.
      const initialCoords = listingsToCoords(listingsRef.current);
      const initialBounds = computeBounds(initialCoords);
      const loc = userLocationRef.current;
      const center =
        initialBounds?.center ??
        (loc ? { lat: loc.lat, lng: loc.lon } : FALLBACK_CENTER);

      createStageRef.current = 'creating';
      const map = await GoogleMap.create({
        id: `home-feed-map-${Date.now()}`,
        element: mapElRef.current,
        apiKey,
        ...getBaseMapLocale(),
        config: {
          ...BASE_MAP_CONFIG,
          center,
          zoom: INITIAL_ZOOM,
          minZoom: MIN_ZOOM,
        },
      });

      if (cancelled) {
        await map.destroy();
        return;
      }
      createdMap = map;
      mapInstanceRef.current = map;
      createStageRef.current = 'listeners';

      await map.setOnMarkerClickListener((event) => {
        const listingsAtMarker = markerToListingsRef.current.get(event.markerId);
        if (!listingsAtMarker || listingsAtMarker.length === 0) return;
        if (listingsAtMarker.length === 1) {
          onMarkerSelectRef.current?.(listingsAtMarker[0]);
        } else {
          // The marker represents a cluster (proximity-grouped at the current
          // zoom) or a same-coord stack — same UX either way: surface the picker.
          onClusterSelectRef.current?.(listingsAtMarker);
        }
      });

      await map.setOnMapClickListener(() => {
        onMapTapRef.current?.();
      });

      // Re-cluster on zoom change. Quantizing the zoom value prevents the
      // marker-sync effect from re-running for tiny camera drifts and during
      // pinch-zoom animation frames.
      await map.setOnCameraIdleListener((data) => {
        const quantized =
          Math.round(data.zoom / ZOOM_QUANTIZATION) * ZOOM_QUANTIZATION;
        setCurrentZoom((prev) => (prev === quantized ? prev : quantized));
      });

      if (cancelled) return;
      createStageRef.current = 'ready';
      setMapReady(true);
      // Slow-but-alive vs dead: if the watchdog already reported this mount,
      // follow up so the two lines read as one incident in Logs Explorer.
      if (watchdogFired) {
        logMapError(MAP_NAME, 'late-ready', new Error('map became ready after watchdog fired'), createContext());
      }
      // A late ready clears a timeout fallback, but never an auth failure.
      if (!hasMapAuthFailed()) reportStatus({ state: 'ready' });
    };

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      window.clearTimeout(watchdogId);
      window.clearTimeout(retryId);
      unsubscribeAuth();
      if (createdMap) {
        void createdMap.destroy();
        createdMap = null;
      }
      mapInstanceRef.current = null;
      currentMarkerIdsRef.current = [];
      markerToListingsRef.current = new Map();
      setMapReady(false);
    };
  }, []);

  // Re-fit the camera to the listings whenever they change (after map ready).
  // Single listing (or all collapsed onto the same obfuscated point) snaps to
  // a fixed mid zoom; otherwise fit the bounding box. The map config's
  // `minZoom` clamps fitBounds so a far-flung outlier won't pull the camera
  // out past the configured floor.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    let cancelled = false;

    const fit = async () => {
      const coords = listingsToCoords(listings);
      if (coords.length === 0) return;
      const bounds = computeBounds(coords);
      if (!bounds) return;

      const latSpan = bounds.northeast.lat - bounds.southwest.lat;
      const lngSpan = bounds.northeast.lng - bounds.southwest.lng;
      const collapsed = latSpan < SAME_POINT_EPSILON && lngSpan < SAME_POINT_EPSILON;

      try {
        if (coords.length === 1 || collapsed) {
          await map.setCamera({
            coordinate: bounds.center,
            zoom: SINGLE_LISTING_ZOOM,
            animate: true,
          });
        } else {
          const llb = new LatLngBounds({
            southwest: bounds.southwest,
            northeast: bounds.northeast,
            center: bounds.center,
          });
          await map.fitBounds(llb, FIT_PADDING_PX);
        }
      } catch (e) {
        if (!cancelled) {
          logMapError(MAP_NAME, 'fit-camera', e, { listingCount: listings.length, coordCount: coords.length });
        }
      }
    };

    void fit();

    return () => {
      cancelled = true;
    };
  }, [listings, mapReady]);

  // Re-sync markers whenever the listings prop changes OR the zoom crosses a
  // quantization boundary. Each visible marker represents a proximity-cluster
  // at the current zoom — single-listing clusters render the dish pin alone;
  // multi-listing clusters add a count badge in the same style. Tap routes to
  // onMarkerSelect / onClusterSelect by group size.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    let cancelled = false;

    const sync = async () => {
      const oldIds = currentMarkerIdsRef.current;
      if (oldIds.length > 0) {
        try {
          await map.removeMarkers(oldIds);
        } catch (e) {
          logMapError(MAP_NAME, 'remove-markers', e, { markerCount: oldIds.length });
        }
      }
      if (cancelled) return;

      // First, collapse same-coord listings (these always belong together
      // regardless of zoom). Then proximity-cluster those groups at the
      // current zoom level so the visual mirrors what MarkerClusterer would
      // do — only with our own icon.
      const sameCoordGroups = new Map<string, CoordGroup>();
      for (const listing of listings) {
        const coord = parseObfuscated(listing.obfuscatedLocation);
        if (!coord || !listing.obfuscatedLocation) continue;
        const existing = sameCoordGroups.get(listing.obfuscatedLocation);
        if (existing) {
          existing.listings.push(listing);
        } else {
          sameCoordGroups.set(listing.obfuscatedLocation, {
            listings: [listing],
            coord,
          });
        }
      }
      const clusters = clusterByProximity([...sameCoordGroups.values()], currentZoom);

      const withIcons = await Promise.all(
        clusters.map(async (cluster) => {
          const rep = cluster.listings[0];
          return {
            cluster,
            icon: await buildDishMarkerIcon(rep.thumbnailUrl ?? rep.imageUrl ?? null, {
              count: cluster.listings.length >= 2 ? cluster.listings.length : undefined,
            }),
          };
        }),
      );

      if (cancelled) return;

      const newIds: string[] = [];
      const idMap = new Map<string, DishListing[]>();
      for (const { cluster, icon } of withIcons) {
        const rep = cluster.listings[0];
        const id = await map.addMarker({
          coordinate: cluster.coord,
          title: rep.title,
          iconUrl: icon.dataUrl,
          iconSize: { width: icon.width, height: icon.height },
          iconAnchor: { x: icon.anchorX, y: icon.anchorY },
        });
        newIds.push(id);
        idMap.set(id, cluster.listings);
      }

      if (cancelled) {
        try {
          await map.removeMarkers(newIds);
        } catch {
          // ignore — we're tearing down
        }
        return;
      }

      currentMarkerIdsRef.current = newIds;
      markerToListingsRef.current = idMap;
    };

    sync().catch((e) => {
      if (cancelled) return;
      logMapError(MAP_NAME, 'sync-markers', e, { listingCount: listings.length, zoom: currentZoom });
    });

    return () => {
      cancelled = true;
    };
  }, [listings, mapReady, currentZoom]);

  return (
    <div
      ref={mapElRef}
      className={`bg-neutral-200 ${className ?? ''}`}
      style={{ touchAction: 'none' }}
    />
  );
};
